import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CandlestickChart,
  History,
  LogOut,
  AlertCircle,
  Home,
  KeyRound,
  Send,
  GraduationCap,
  LineChart,
  Wallet,
  Smartphone,
  Rocket,
  TrendingUp,
  Target,
  Dices,
  UserRound,
  Ship,
  Gamepad2,
} from "lucide-react";
import { TELEGRAM_URL, DERIV_AFFILIATE_URL } from "@/lib/socialLinks";
import { getAdSrc } from "@/lib/adtrack";
import Logo from "@/components/marketing/Logo";
import BalanceCard from "@/components/dashboard/BalanceCard";
import BotLeaderboard from "@/components/dashboard/BotLeaderboard";
import Over3RecoveryBot from "@/components/dashboard/Over3RecoveryBot";
import FundingGuide from "@/components/dashboard/FundingGuide";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ensureCountry } from "@/lib/country";
import AddToHomeGuide from "@/components/dashboard/AddToHomeGuide";
// InstallBanner popup removed Oct 2026 (user request — blocked taps on mobile)
import ProTrade from "@/components/dashboard/ProTrade";
import LeaderboardCard from "@/components/dashboard/LeaderboardCard";
import MyBotStats from "@/components/dashboard/MyBotStats";
import HeliRide from "@/components/dashboard/HeliRide";
import HigherLow from "@/components/dashboard/HigherLow";
import WinPopup from "@/components/dashboard/WinPopup";
import ForceNicknameModal from "@/components/dashboard/ForceNicknameModal";
import useManualSession from "@/hooks/useManualSession";
import LiveTradeFeed from "@/components/dashboard/LiveTradeFeed";
// TradingBot ("Other strategies") removed from the user dashboard per user request.
// Cloud Bot panel/health-dot removed per user request (Auto-FX is now the primary bot).
import RecentTradesInline from "@/components/dashboard/RecentTradesInline";
import ActiveTradeRibbon from "@/components/dashboard/ActiveTradeRibbon";
import ConnectTokenDialog from "@/components/dashboard/ConnectTokenDialog";
import OnboardingTour from "@/components/dashboard/OnboardingTour";
import TutorialModal from "@/components/dashboard/TutorialModal";
import TelegramLinkCard from "@/components/dashboard/TelegramLinkCard";
import StreakCard from "@/components/dashboard/StreakCard";
import HiLoCards from "@/components/dashboard/HiLoCards";
import ProfileTab from "@/components/dashboard/ProfileTab";
// FleetCommander import removed — Fleet feature deleted (owner request)
import ArcadeHub from "@/components/dashboard/ArcadeHub";
import RiskSafetyBar, { RiskConsentCard, hasRiskConsent } from "@/components/dashboard/RiskSafetyBar";
import ConservativeFxBot from "@/components/dashboard/ConservativeFxBot";

const BOT_TABS = ["hilo", "cards", "heli", "risefall"];
import { useAuth } from "@/hooks/useAuth";
import { useConfig } from "@/hooks/useConfig";
import { setAppId, connectPublic, connectAuthenticated, authorize, send, subscribe, forget, teardownAuth, isRealDerivLoginId } from "@/lib/deriv";
import { api } from "@/lib/api";
import EmailCaptureDialog from "@/components/EmailCaptureDialog";
import { toast } from "sonner";

// Tabs that drive the mobile (and primary) layout — Deriv-style focused panels.
const TABS = [
  // v9.3 — ⭐ stars ONLY on the measured shootout leaders (Over2/Trade,
  // SNIPER/Rise-Fall); High Low + Rocket stars removed per user.
  { key: "hilo", label: "High Low", icon: Target },
  { key: "cards", label: "HiLo Cards", icon: Dices },
  { key: "heli", label: "Rocket", icon: Rocket },
  { key: "arcade", label: "Arcade", icon: Gamepad2 },
  { key: "trade", label: "Trade", icon: CandlestickChart, star: true },
  { key: "pro", label: "Pro Trade", icon: LineChart },
  { key: "risefall", label: "Rise/Fall", icon: TrendingUp, star: true },
  { key: "profile", label: "Profile", icon: UserRound },
  { key: "history", label: "History", icon: History },
];

const DEMO_BALANCE = 10000;
const DEMO_ACCOUNT_ID = "DEMO";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, logout, switchAccount, login, isAuthed } = useAuth();
  const { config } = useConfig();
  const [balance, setBalance] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  // v8.6 — Manual TRADE tab (Rise/Fall + Digits) only trades on Deriv
  // VOLATILITY SYNTHETIC indices. Forex/Gold/Crypto are bot-only because
  // (a) Deriv doesn't offer Digits on those assets and (b) Rise/Fall on
  // forex requires minute/hour durations which break the tick-based UX
  // the manual panel is optimised for.
  // v8.8d — user explicitly picked R_100 (Volatility 100) as the default
  // landing symbol — most-liquid synthetic with 100s of trades/min so the
  // chart starts populated immediately on first load.
  const [symbol, setSymbol] = useState("R_100");
  // 3-state connection: connecting -> live | demo (fallback when Deriv WS auth fails)
  const [connState, setConnState] = useState("connecting");
  const [activeTab, setActiveTab] = useState("trade");
  const [riskConsent, setRiskConsent] = useState(hasRiskConsent());
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [fundGuideOpen, setFundGuideOpen] = useState(false); // "How to add funds" modal (header, all tabs)

  // 🌍 attach the trader's country flag to their public profile (one-time)
  // + heartbeat so the traders-by-country strip counts them as active today
  useEffect(() => {
    if (!session?.account_id) return;
    ensureCountry(session.account_id);
    const ping = () => api.post("/presence", { account_id: session.account_id, src: getAdSrc() || undefined }).catch(() => {});
    ping();
    const t = setInterval(ping, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [session?.account_id]);
  // 📧 lifecycle email presence ping — works via stored email, or via the
  // account alone when the email is on file from another device
  useEffect(() => {
    let e = "";
    let known = "";
    try {
      e = localStorage.getItem("vts_email_optin_v1") || "";
      known = localStorage.getItem("vts_email_known_v1") || "";
    } catch { /* noop */ }
    const acct = session?.account_id || "";
    if (e.includes("@")) {
      api.post("/marketing/activity", {
        email: e, account_id: acct, is_real: isRealDerivLoginId(acct),
      }).catch(() => {});
    } else if (known === "1" && acct) {
      api.post("/marketing/activity", { account_id: acct }).catch(() => {});
    }
  }, [session?.account_id]);
  const [a2hsOpen, setA2hsOpen] = useState(false); // "Add to Home Screen" guide
  const [emailCaptureOpen, setEmailCaptureOpen] = useState(false); // 📧 post-connect email ask
  // 🔒 trade engines dispatch this when a play is attempted with no email on file
  useEffect(() => {
    const open = () => setEmailCaptureOpen(true);
    window.addEventListener("vts-open-email-gate", open);
    return () => window.removeEventListener("vts-open-email-gate", open);
  }, []);
  // ⚠️ duplicate-tab guard — two open VTS tabs share ONE Deriv rate limit and
  // cause "rate limit for proposal" errors
  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel("vts_tab_guard");
      const warn = () => toast.warning("⚠️ VTS is open in another tab", {
        description: "Two tabs share one Deriv rate limit — close the other tab to avoid 'rate limit' errors.",
        duration: 12000,
      });
      bc.onmessage = (e) => {
        if (e.data === "ping") { bc.postMessage("pong"); warn(); }
        else if (e.data === "pong") warn();
      };
      bc.postMessage("ping");
    } catch { /* unsupported browser */ }
    return () => { try { bc?.close(); } catch { /* noop */ } };
  }, []);

  // ❌ auto-show install popups REMOVED (Oct 2026, user request — the popup
  // blocked taps on mobile). Install guide stays reachable via the header button.
  const subsRef = useRef([]);

  // Auto-open the token dialog when arriving via "Start Trading" CTAs
  // (URL has `?connect=1`). Unauthenticated users land here in Demo mode and
  // can connect their real Deriv account via API token without OAuth.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("connect") === "1" && !isAuthed) {
      setTokenDialogOpen(true);
    }
    // v8.9.127 — `?connect=oauth` (set by the AuthCallback "Try Again"
    // button) jumps straight to the Deriv OAuth dialog with the OAuth
    // pre-selected. Same dialog, just shortcut.
    if (params.get("connect") === "oauth" && !isAuthed) {
      setTokenDialogOpen(true);
    }
    // ⭐ `?tab=risefall` (landing featured card) — deep-link straight to a tab.
    const tab = params.get("tab");
    if (tab && TABS.some((t) => t.key === tab)) {
      setActiveTab(tab);
    }
  }, [location.search, isAuthed]);

  // Bind a stored ?ref= referral code to the account once the user connects.
  useEffect(() => {
    const code = localStorage.getItem("vts_ref_code");
    if (!code || !isAuthed || !session?.account_id) return;
    api.post("/referral/bind", { account_id: session.account_id, ref_code: code })
      .then(({ data }) => {
        if (data?.ok) toast.success(`Referral linked — referred by ${data.referrer}`);
        localStorage.removeItem("vts_ref_code");
      })
      .catch(() => {});
  }, [isAuthed, session?.account_id]);

  // v8.9.127 — DEFENSIVE OAuth-callback fallback. If Deriv (or a misconfigured
  // app on Deriv's side) redirects the user back to `/trade?code=…&state=…`
  // INSTEAD OF `/auth/callback?code=…`, the AuthCallback page never runs and
  // the user is left "logged out forever". This handler intercepts such a
  // landing, forwards them to the proper /auth/callback URL so the existing
  // PKCE exchange logic runs, and they end up authenticated.
  useEffect(() => {
    if (isAuthed) return;
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    // Heuristic: a `code` param plus a `state` param ≈ Deriv OAuth callback.
    // Standard query-param matches Deriv's PKCE response format.
    if (code && params.get("state")) {
      navigate(`/auth/callback${location.search}`, { replace: true });
      return;
    }
    // Legacy implicit hash flow: #token1=…&acct1=…. Same idea — route
    // it through /auth/callback so the existing handler does the login().
    const hash = window.location.hash || "";
    if (hash.includes("token1=") && hash.includes("acct1=")) {
      navigate(`/auth/callback${hash}`, { replace: true });
    }
  }, [location.search, isAuthed, navigate]);

  // Connect to Deriv (new architecture):
  // 1) Open the PUBLIC websocket immediately — gives us tick streams + charts.
  // 2) If we have a session token + account_id, ask the backend for an OTP
  //    URL and UPGRADE to the authenticated socket for balance + trading.
  useEffect(() => {
    if (!config) return;
    setAppId(config.deriv_app_id);
    let cancelled = false;
    // Reset state on each (re)connect so the status pill flips visibly
    setConnState((s) => (s === "live" ? "connecting" : s));

    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setConnState((s) => (s === "connecting" ? "demo" : s));
    }, 17000);   // v8.9.146 — 9s → 17s: the OTP socket has a 15s open timeout,
                 // so a 9s fallback flashed "demo" (looked like a disconnect)
                 // while the authenticated socket was still legitimately
                 // connecting, then snapped back to "live". 17s outlasts the
                 // WS open timeout so the pill no longer flickers on slow links.

    (async () => {
      // Step 1 — public socket. Legacy ws.derivws.com requires a NUMERIC app
      // ID (we use config.deriv_ws_app_id which defaults to 1089). Alphanumeric
      // Ory IDs from auth.deriv.com return HTTP 401 on the legacy WS, so we
      // never pass them here.
      try {
        await connectPublic("wss://api.derivws.com/trading/v1/options/ws/public");
      } catch {
        if (!cancelled) setConnState("demo");
        return;
      }

      // No session? Stay on demo (we can still browse charts/strategies)
      if (!session?.token || !session?.account_id) {
        if (!cancelled) setConnState("demo");
        return;
      }

      // Self-heal (prod RCA Oct'26): if the active account id isn't a real
      // Deriv login id (e.g. the "OAUTH_USER" placeholder saved when the
      // /deriv/accounts fetch failed during the OAuth callback), re-fetch the
      // account list now and rewrite the session with REAL ids. This is what
      // broke "switch to demo" — the demo entry carried OAUTH_USER, which
      // Deriv's OTP endpoint can't authenticate.
      if (session.connection_type === "otp" && !isRealDerivLoginId(session.account_id)) {
        try {
          const acctsRes = await api.get("/deriv/accounts", {
            headers: { Authorization: `Bearer ${session.token}` },
            validateStatus: () => true,
          });
          const raw = acctsRes.data?.data || acctsRes.data || {};
          const list = Array.isArray(raw.accounts) ? raw.accounts : Array.isArray(raw) ? raw : [];
          const mapped = list.map((a) => {
            const id = a.id || a.account_id || a.loginid;
            const isVirtual = a.is_virtual === true || a.virtual === true ||
              a.account_type === "virtual" || a.account_type === "demo" ||
              a.type === "virtual" || a.type === "demo" || /^(VRTC|VRW|DOT)/i.test(id || "");
            return { token: session.token, account_id: id, currency: a.currency,
              balance: a.balance, is_virtual: isVirtual,
              account_type: a.account_type || a.type || (isVirtual ? "virtual" : "real") };
          }).filter((a) => isRealDerivLoginId(a.account_id));
          if (mapped.length && !cancelled) {
            const real = mapped.find((a) => !a.is_virtual) || mapped[0];
            login({ ...session, account_id: real.account_id,
              currency: real.currency || session.currency, accounts: mapped });
            return; // session change re-triggers this effect with a valid id
          }
        } catch { /* fall through to demo */ }
        if (!cancelled) setConnState("demo");
        return;
      }

      // Step 2 — try to upgrade to authenticated WS.
      // Three paths, in order of preference:
      //  (a) connection_type === 'otp' → fetch a FRESH OTP URL (they're
      //      short-lived) via /api/deriv/otp/{id} and connect there. The
      //      backend forwards the partner Deriv-App-ID so the 3% markup
      //      credits to us. This is the proper documented Deriv flow.
      //  (b) connection_type === 'api_token' → legacy WS authorize.
      //  (c) Otherwise (legacy OAuth) → connectAuthenticated by account ID.
      try {
        if (session.connection_type === "otp") {
          await connectAuthenticated(session.account_id, session.token);
          if (cancelled) return;
          setConnState("live");
        } else if (session.connection_type === "api_token") {
          const authResp = await authorize(session.token);
          if (authResp?.error) {
            throw new Error(authResp.error.message || "Token rejected");
          }
          if (cancelled) return;
          setConnState("live");
          if (authResp.authorize) {
            setBalance({
              balance: authResp.authorize.balance,
              currency: authResp.authorize.currency,
              loginid: authResp.authorize.loginid,
            });
          }
        } else {
          await connectAuthenticated(session.account_id, session.token);
          if (cancelled) return;
          setConnState("live");
        }

        // Fetch balance. We use a SINGLE `send` request + an interval poll
        // (every 15s) instead of the streaming subscribe pattern — the new
        // Options WS sometimes doesn't dispatch updates the way subscribe
        // expects, leaving the header stuck on "—". A simple poll is far
        // more reliable and the bandwidth cost is negligible.
        const fetchBalance = async () => {
          try {
            const res = await send({ balance: 1 });
            if (res?.balance) {
              setBalance({
                balance: parseFloat(res.balance.balance),
                currency: res.balance.currency,
                loginid: res.balance.loginid,
              });
              // heartbeat for the admin User Trades panel (fire-and-forget)
              api.post("/track/balance", {
                account_id: res.balance.loginid,
                balance: parseFloat(res.balance.balance),
                currency: res.balance.currency || "USD",
              }).catch(() => {});
            } else if (res?.error) {
              // eslint-disable-next-line no-console
              console.warn("[VTS] balance fetch error:", res.error);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[VTS] balance fetch threw:", e?.message);
          }
        };
        await fetchBalance();
        const balanceInterval = setInterval(fetchBalance, 15000);
        subsRef.current.push({ __interval: balanceInterval });

        // 📧 HARD GATE: Deriv's new API exposes NO user email (verified), so we
        // ask once after connect. Never re-ask if this device OR this Deriv
        // account already saved one (server check covers new devices).
        try {
          let prev = "";
          try { prev = localStorage.getItem("vts_email_optin_v1") || ""; } catch { /* noop */ }
          if (!prev.includes("@")) {
            let known = false;
            try {
              const { data } = await api.get(`/marketing/email-status/${encodeURIComponent(session.account_id || "")}`);
              known = !!data?.known;
            } catch { /* offline — fall through to ask */ }
            if (known) {
              try { localStorage.setItem("vts_email_known_v1", "1"); } catch { /* noop */ }
              window.dispatchEvent(new Event("vts-email-prompt-done")); // unblock Smart Stake
            } else {
              setEmailCaptureOpen(true);
            }
          }
        } catch { /* non-blocking */ }

        // Fetch portfolio once
        const port = await send({ portfolio: 1 });
        if (port?.portfolio?.contracts) setPortfolio(port.portfolio.contracts);
      } catch (e) {
        if (!cancelled) {
          // Detect Deriv token expiry (401 / InvalidToken / specific token-
          // expiry strings). When this happens we MUST clear the session —
          // otherwise the header still says "Live" but every trade silently
          // fails.
          //
          // Feb 2026 v7.6 — tightened the regex per user report
          // "everytime I log in it logs out". The previous version matched
          // ANY error containing the word "session" — including transient
          // WebSocket reconnect errors ("session closed", "session
          // terminated") that fire normally during the initial connection.
          // Now we require an explicit Deriv error code OR one of the
          // very specific token-failure strings Deriv documents.
          const errMsg = (e?.response?.data?.error || e?.message || "").toString();
          const errCode = (e?.response?.data?.error_code || e?.error_code || "").toString();
          const status = e?.response?.status;
          const isAuthFailure =
            status === 401 ||
            errCode === "InvalidToken" ||
            errCode === "AuthorizationRequired" ||
            errCode === "DisabledClient" ||
            /\binvalid[\s_-]?token\b|\btoken[\s_-]?expired\b|\bauthorization required\b/i.test(errMsg);
          if (isAuthFailure && session?.token) {
            // v8.9.126 — User report: "Now I'm unable to log in, it keeps
            // logging me out." Previously this branch called `logout()`
            // which wiped the session entirely the moment Deriv WS replied
            // with InvalidToken — but Deriv's WS auth is flaky (especially
            // immediately after OAuth) and often returns InvalidToken once,
            // then succeeds on the next reconnect. Auto-logging out
            // bounces the user back to a fresh state every time.
            //
            // New behaviour: KEEP the session in localStorage, drop to demo
            // mode, show a non-blocking toast with a "Re-connect Deriv"
            // CTA. If the token really is dead, the user can sign out
            // manually; otherwise the next auth attempt usually succeeds.
            setConnState("demo");
            toast.error("Deriv connection issue", {
              description:
                "Deriv WS rejected your token. You're still signed in — tap your account in the header to re-link Deriv if needed.",
              duration: 8000,
            });
            return;
          }
          // Non-auth failure (network blip, transient WS error) — just
          // drop to demo mode without nuking the session. User stays
          // logged in and the next reconnect attempt may succeed.
          setConnState("demo");
          toast.message("Demo mode active", {
            description:
              "Couldn't open an authenticated Deriv session — you can still preview charts, strategies and the bot.",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      subsRef.current.forEach((entry) => {
        if (entry && typeof entry === "object" && entry.__interval) {
          clearInterval(entry.__interval);
        } else {
          forget(entry);
        }
      });
      subsRef.current = [];
    };
  }, [config, session?.token, session?.account_id, session?.connection_type]);

  const refreshPortfolio = useCallback(async () => {
    // v8.9.125 — Always pull backend-tracked OPEN trades regardless of
    // connState. Demo accounts (D0T…/VRTC) can't fetch Deriv's portfolio
    // over the public WS (no auth), so without this merge the Live Trades
    // panel sat at "0 open" even while the Auto-FX bot was firing.
    // Backend trade_events already has every Auto-FX + manual trade mirror.
    const backendOpenP = (async () => {
      const acctId = session?.account_id || DEMO_ACCOUNT_ID;
      if (!acctId) return [];
      try {
        const { data } = await api.get("/track/trades", {
          params: { account_id: acctId, status: "open", limit: 50 },
        });
        return Array.isArray(data?.trades) ? data.trades : [];
      } catch (_e) {
        return [];
      }
    })();

    let derivContracts = [];
    if (connState === "live") {
      try {
        const port = await send({ portfolio: 1 });
        derivContracts = Array.isArray(port?.portfolio?.contracts)
          ? port.portfolio.contracts
          : [];
      } catch (_e) {
        /* ignore */
      }
    }

    const backendOpen = await backendOpenP;
    // Normalise backend rows to the shape LiveTradeFeed expects.
    const backendShaped = backendOpen.map((t) => ({
      contract_id: t.contract_id,
      symbol: t.symbol,
      contract_type: t.contract_type || t.direction,
      buy_price: t.stake,
      payout: t.payout,
      entry_spot: t.entry_spot ?? t.entry_tick ?? null,
      entry_tick_time: t.entry_tick_time || t.created_at,
      date_start: t.date_start || t.created_at,
      purchase_time: t.purchase_time || t.created_at,
      date_expiry: t.date_expiry,
      strategy: t.strategy,
      __source: "backend",
    }));

    // De-dupe: Deriv portfolio is authoritative for the live P&L stream,
    // backend-tracked rows fill the gap on Demo (or for bot trades not yet
    // visible in Deriv's portfolio).
    const seen = new Set(derivContracts.map((c) => String(c.contract_id)));
    const merged = [
      ...derivContracts,
      ...backendShaped.filter((c) => !seen.has(String(c.contract_id))),
    ];
    setPortfolio(merged);
  }, [connState, session?.account_id]);

  // Auto-poll portfolio every 5s while on the Trade tab so new contracts (from
  // the bot or manual trades) show up in the Live Trade Feed automatically.
  // v8.9.125 — no longer gated on `connState === "live"` so Demo accounts
  // also get periodic refreshes of backend-tracked open trades.
  useEffect(() => {
    if (activeTab !== "trade" && activeTab !== "risefall") return;
    const t = setInterval(refreshPortfolio, 5000);
    return () => clearInterval(t);
  }, [activeTab, refreshPortfolio]);

  // Instant refresh when the bot (or any other component) opens a new
  // contract — eliminates the up-to-5s lag before the new trade appears in
  // the Live Trade Feed + Active Trade Ribbon.
  useEffect(() => {
    const onOpened = () => {
      refreshPortfolio();
      // Re-poll once more 1.5s later to catch any backend lag on Deriv's side
      // before they include the brand-new contract in the portfolio list.
      setTimeout(refreshPortfolio, 1500);
    };
    window.addEventListener("vts:contract-opened", onOpened);
    return () => window.removeEventListener("vts:contract-opened", onOpened);
  }, [refreshPortfolio]);

  // Remove a contract from the local list once it settles (it lives in /history now)
  const handleContractSettled = useCallback((cid) => {
    setPortfolio((cur) => cur.filter((c) => String(c.contract_id) !== String(cid)));
    // Refresh balance is handled by the balance subscription
  }, []);

  const handleLogout = () => {
    // v8.9.129 — stop the silent OTP refresh loop so we don't leak a timer
    // pinging /deriv/otp after the user has signed out.
    teardownAuth();
    logout();
    toast.success("Signed out");
    navigate("/");
  };

  const isLive = connState === "live";
  const isDemo = connState === "demo";
  const isConnecting = connState === "connecting";
  // markup_ready is true once OAuth + OTP succeeds (any account type).
  // But markup only credits on REAL accounts — check the current active
  // account's `is_virtual` flag from session.accounts.
  const activeAccount = (session?.accounts || []).find(
    (a) => a.account_id === session?.account_id,
  );
  const isVirtualAccount = !!activeAccount?.is_virtual ||
    /^(VRTC|DOT)/i.test(session?.account_id || "");
  const markupReady = !!session?.markup_ready && !isVirtualAccount;

  // Effective display values — demo mode shows synthetic balance so the UI feels alive.
  // For live mode, prefer the WS-polled `balance`, but fall back to the
  // OAuth-cached account record if the WS hasn't dispatched yet (some Deriv
  // accounts return a `send({balance:1})` reply with no payload — without
  // this fallback the header sits on "—" forever even though we know the
  // balance from the session.accounts[] array).
  const sessionAcct = session?.accounts?.find((a) => (a.loginid || a.account_id) === session?.account_id);
  const displayBalance = isLive
    ? (balance && balance.balance != null
        ? balance
        : sessionAcct
          ? { balance: Number(sessionAcct.balance), currency: sessionAcct.currency || "USD", loginid: sessionAcct.loginid || sessionAcct.account_id }
          : balance)
    : { balance: DEMO_BALANCE, currency: "USD", loginid: DEMO_ACCOUNT_ID };
  const displayAccountId = isLive
    ? balance?.loginid || session?.account_id
    : `${DEMO_ACCOUNT_ID} · ${session?.account_id || ""}`.trim();

  // v8.9.12 (Feb 2026) — shared manual-trading session for the
  // DigitMatches panel. (Rise/Fall TradePanel + ManualTargetBar were
  // removed in v8.9.123 so we no longer need start/reset/update.)
  const manualAccountId = isLive ? session?.account_id : DEMO_ACCOUNT_ID;
  const { session: manualSession, recordTrade: recordManualTrade } =
    useManualSession(manualAccountId);

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-vts-bg">
      <WinPopup />
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-vts-line bg-black/80 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 h-14 lg:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
          </div>
          <div className="flex items-center gap-0.5 sm:gap-2 min-w-0">
            <LanguageSwitcher compact />
            <button
              data-testid="back-to-site-btn"
              onClick={() => navigate("/")}
              className="hidden sm:flex items-center gap-1.5 text-xs text-vts-silver/70 hover:text-white px-2 sm:px-3 py-2 rounded-md hover:bg-vts-surface transition"
              aria-label="Home"
            >
              <Home className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Home</span>
            </button>
            <button
              data-testid="open-tutorial-btn"
              onClick={() => setTutorialOpen(true)}
              className="flex items-center gap-1.5 text-xs text-vts-blue hover:text-white px-1.5 sm:px-3 py-2 rounded-md border border-vts-blue/30 bg-vts-blue/10 hover:bg-vts-blue/20 transition"
              aria-label="How to use VTS"
              title="How to trade · strategies · safety"
            >
              <GraduationCap className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap hidden sm:inline">How to use</span>
            </button>
            <button
              data-testid="header-add-funds-btn"
              onClick={() => setFundGuideOpen(true)}
              className="flex items-center gap-1.5 text-xs text-amber-300 hover:text-white px-1.5 sm:px-3 py-2 rounded-md border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/25 transition"
              aria-label="How to add funds"
              title="Deposit on Deriv → transfer to your Options account"
            >
              <Wallet className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap hidden sm:inline">Add funds</span>
            </button>
            <button
              data-testid="header-a2hs-btn"
              onClick={() => setA2hsOpen(true)}
              className="hidden sm:flex items-center gap-1.5 text-xs text-vts-silver/80 hover:text-white px-2 sm:px-3 py-2 rounded-md border border-vts-line bg-vts-surface/40 hover:bg-vts-surface transition"
              aria-label="Add to Home Screen"
              title="Add VTS to your phone's Home Screen"
            >
              <Smartphone className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap hidden lg:inline">Get the app</span>
            </button>
            {(isDemo || isConnecting) && (
              <button
                data-testid="header-connect-token-btn"
                onClick={() => setTokenDialogOpen(true)}
                className="flex items-center gap-1.5 bg-vts-blue hover:bg-vts-blueHover text-white text-xs font-medium px-2.5 sm:px-3 py-2 rounded-md transition shadow-lg shadow-vts-blue/20"
              >
                <KeyRound className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Login with Deriv</span>
              </button>
            )}
            <span
              data-testid="dashboard-status-pill"
              className={`hidden sm:inline-flex text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-mono border ${
                isLive
                  ? "bg-vts-profit/15 text-vts-profit border-vts-profit/30"
                  : isDemo
                  ? "bg-vts-blue/15 text-vts-blue border-vts-blue/40"
                  : "bg-vts-surface text-vts-silver/70 border-vts-line"
              }`}
            >
              {isLive && "● Live"}
              {isDemo && "● Demo"}
              {isConnecting && "○…"}
            </span>
            {/* Cloud Bot health pulse removed per user request. */}
            {/* Account switcher — appears when the OAuth-fetched accounts list
                has more than one entry. Lets the user swap between e.g. their
                virtual demo and their real CR/MF account. */}
            {isAuthed && Array.isArray(session?.accounts) && session.accounts.length > 1 && (
              <select
                data-testid="account-switcher"
                value={session.account_id}
                onChange={(e) => switchAccount(e.target.value)}
                title="Switch Deriv account"
                className="bg-vts-surface border border-vts-line text-white text-xs font-mono rounded-md px-1 sm:px-2 py-1.5 hover:bg-vts-surfaceHover transition cursor-pointer min-w-0 shrink max-w-[68px] sm:max-w-[180px]"
              >
                {session.accounts.map((a) => {
                  const id = a.account_id || "";
                  const shortId = id.length > 9 ? `${id.slice(0, 3)}…${id.slice(-4)}` : id;
                  return (
                    <option key={a.account_id} value={a.account_id}>
                      {a.is_virtual ? "Demo" : "Real"} · {shortId}
                      {a.currency ? ` (${a.currency})` : ""}
                    </option>
                  );
                })}
              </select>
            )}
            {/* Always-on Telegram CTA — small icon button so users can find
                the trading group from any logged-in page (hidden on mobile to
                save header space; users can find it in the demo banner & footer). */}
            <a
              data-testid="dashboard-telegram-link"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="hidden sm:flex items-center gap-1.5 text-xs px-2 py-2 rounded-md text-[#229ED9] hover:text-white hover:bg-[#229ED9] transition"
              aria-label="Join VTS Telegram group"
              title="Join VTS Telegram group"
            >
              <Send className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Telegram</span>
            </a>
            <button
              data-testid="dashboard-logout-btn"
              onClick={handleLogout}
              disabled={!isAuthed}
              className={`flex items-center gap-1.5 text-xs px-2 py-2 rounded-md transition ${
                isAuthed
                  ? "text-vts-loss/80 hover:text-vts-loss hover:bg-vts-loss/10"
                  : "text-vts-silver/30 cursor-not-allowed"
              }`}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* Demo banner */}
        {isDemo && (
          <div
            data-testid="demo-mode-banner"
            className={`bg-vts-blue/10 border-t border-vts-blue/30 ${activeTab === "pro" ? "hidden lg:block" : ""}`}
          >
            <div className="mx-auto max-w-7xl px-4 lg:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 text-[11px] sm:text-xs">
              <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0">
                <AlertCircle className="h-3.5 w-3.5 text-vts-blue shrink-0 mt-0.5 sm:mt-0" />
                <span className="text-vts-silver/80">
                  <strong className="text-white">Demo mode.</strong> Log in with
                  your Deriv account to trade real markets — no API token needed.
                </span>
              </div>
              <div className="flex gap-2 shrink-0">
                <a
                  data-testid="banner-create-account-link"
                  href={DERIV_AFFILIATE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 border border-vts-line hover:border-vts-blue text-vts-silver hover:text-white text-xs font-medium px-4 py-1.5 rounded-md transition whitespace-nowrap"
                >
                  New to Deriv? Create account
                </a>
                <button
                  data-testid="banner-connect-token-btn"
                  onClick={() => setTokenDialogOpen(true)}
                  className="flex items-center justify-center gap-1.5 bg-vts-blue hover:bg-vts-blueHover text-white text-xs font-medium px-4 py-1.5 rounded-md transition whitespace-nowrap"
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  Login with Deriv
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Virtual account notice — purely informational, no mention of
            partner-side mechanics (markup, etc.) to keep things end-user friendly. */}
        {isLive && isVirtualAccount && (
          <div
            data-testid="virtual-account-banner"
            className={`bg-amber-500/10 border-t border-amber-500/30 ${activeTab === "pro" ? "hidden lg:block" : ""}`}
          >
            <div className="mx-auto max-w-7xl px-4 lg:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 text-[11px] sm:text-xs">
              <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0">
                <AlertCircle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5 sm:mt-0" />
                <span className="text-vts-silver/90">
                  <strong className="text-amber-300">Demo account active.</strong>{" "}
                  You&apos;re connected to <span className="font-mono text-white">{session?.account_id}</span>{" "}
                  — trades run with practice funds.
                  {Array.isArray(session?.accounts) && session.accounts.length > 1
                    ? " Switch to a real account using the dropdown in the header above to trade real markets."
                    : " Add a real-money account in Deriv to trade real markets."}
                </span>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* 🎁 Referral promo — visible on the main dashboard, links to Profile tab */}
      {activeTab !== "profile" && (
        <div data-testid="referral-promo-strip" className="bg-gradient-to-r from-amber-500/10 via-vts-blue/5 to-transparent border-b border-amber-500/20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8 py-2 flex items-center gap-2 text-[11px] sm:text-xs">
            <span className="shrink-0">🎁</span>
            <span className="text-vts-silver/85 min-w-0 truncate">
              <strong className="text-amber-300">Refer friends — earn up to 20%</strong>
              <span className="hidden sm:inline"> lifetime commission on the trading of everyone you invite.</span>
            </span>
            <button
              data-testid="referral-promo-cta"
              onClick={() => {
                setActiveTab("profile");
                // the profile tab loads cards async (they push the page around),
                // so re-aim at the referral panel a few times until it settles
                [350, 1000, 1800].forEach((ms) =>
                  setTimeout(() => document.getElementById("referral-panel")?.scrollIntoView({ behavior: ms === 1800 ? "smooth" : "auto", block: "start" }), ms));
              }}
              className="ml-auto shrink-0 px-3 py-1 rounded-md bg-amber-400/15 border border-amber-400/40 text-amber-300 hover:bg-amber-400/25 font-bold transition whitespace-nowrap"
            >
              Get my link →
            </button>
          </div>
        </div>
      )}
      {fundGuideOpen && <FundingGuide onClose={() => setFundGuideOpen(false)} />}
      {a2hsOpen && <AddToHomeGuide onClose={() => setA2hsOpen(false)} />}
      <EmailCaptureDialog open={emailCaptureOpen} accountId={session?.account_id || ""}
        onClose={() => {
          setEmailCaptureOpen(false);
          window.dispatchEvent(new Event("vts-email-prompt-done")); // lets Smart Stake take its turn
        }} />
      {/* ❌ InstallBanner popup removed (Oct 2026, user request) */}

      {/* API token connect dialog */}
      <ConnectTokenDialog
        open={tokenDialogOpen}
        onOpenChange={setTokenDialogOpen}
        onConnected={() => setConnState("live")}
      />

      {/* First-time onboarding tour (4 steps · localStorage gated) */}
      <OnboardingTour />

      {/* On-demand tutorial — opens from the "How to use" header button.
          Comprehensive guide covering strategies, parallel vs single,
          safety rules, and honest expectations. */}
      <TutorialModal open={tutorialOpen} onOpenChange={setTutorialOpen} />

      {/* Main content area */}
      <main className="mx-auto max-w-7xl px-4 lg:px-8 pb-36 lg:pb-10 pt-4 lg:pt-6">
        {/* Balance — hidden on the Pro Trade tab; a compact floating balance
            widget renders on the chart instead so candles own the screen */}
        <div className={`mb-4 ${activeTab === "pro" ? "hidden" : ""}`}>
          <BalanceCard
            balance={displayBalance}
            accountId={displayAccountId}
            connState={connState}
            onAddFunds={() => setFundGuideOpen(true)}
          />
        </div>

        {/* Tabs (mobile = bottom nav, desktop = inline segmented control) */}
        <div className="hidden lg:flex items-center gap-1 mb-5 bg-vts-surface/60 border border-vts-line rounded-lg p-1 w-fit">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`desktop-tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                  activeTab === t.key
                    ? "bg-vts-blue text-white shadow-lg shadow-vts-blue/20"
                    : "text-vts-silver/70 hover:text-white hover:bg-vts-surfaceHover"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
                {t.star && (
                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-amber-400/15 border border-amber-400/40 text-amber-300 leading-none">⭐ TOP</span>
                )}
                {t.fun && (
                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-vts-loss/10 border border-vts-loss/40 text-vts-loss/70 leading-none">FUN</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active panel */}
        <div className="space-y-4">
          {/* 🛡️ Risk & Safety — consent gate + always-visible limits on bot tabs */}
          {BOT_TABS.includes(activeTab) && !riskConsent && (
            <RiskConsentCard onAccept={() => setRiskConsent(true)} />
          )}
          {BOT_TABS.includes(activeTab) && riskConsent && (
            <RiskSafetyBar accountId={session?.account_id} />
          )}
          <div style={{ display: BOT_TABS.includes(activeTab) && !riskConsent ? "none" : "block" }} className="space-y-4">
          {/* 🧪 EXPERIMENTAL banner — these modes must never look safer
              than the primary Controlled FX product (user spec). */}
          {["hilo", "cards", "heli"].includes(activeTab) && (
            <div data-testid="experimental-banner" className="rounded-md border border-orange-400/40 bg-orange-400/10 px-3 py-2 mb-3">
              <p className="text-[10px] font-mono text-orange-300 font-bold">🧪 EXPERIMENTAL MODE</p>
              <p className="text-[9px] font-mono text-vts-silver/70">
                Higher risk than the primary Controlled Forex Rise/Fall bot. Not recommended as your main strategy. You can lose money.
              </p>
            </div>
          )}
          {/* Trade tab — v8.9.100 — switched from conditional mount to
              CSS-hidden (`display:none`) so navigating to BOT no longer
              unmounts `AutoTradeTopN` and wipes its burst/auto-trade
              session. Same trick as the Bot tab below. */}
          {/* Pro Trade tab — Quotex/Pocket-Option-style manual forex trading. */}
          <div style={{ display: activeTab === "pro" ? "block" : "none" }}>
            <ProTrade balance={displayBalance} />
          </div>

          <div style={{ display: activeTab === "heli" ? "block" : "none" }}>
            <HeliRide
              authorized={isLive}
              currency={displayBalance?.currency || "USD"}
              balance={Number(displayBalance?.balance) || 0}
              onConnect={() => setTokenDialogOpen(true)}
            />
          </div>

          {/* Rise/Fall tab — ONLY the Conservative FX bot (user mandate,
              Jun 2026): momentum panel + old Auto-FX panel removed. */}
          <div style={{ display: activeTab === "risefall" ? "block" : "none" }}>
            <div className="space-y-4">
              <ConservativeFxBot
                authorized={isLive}
                accountId={session?.account_id}
                accessToken={session?.token || ""}
                balance={Number(displayBalance?.balance) || 0}
                onConnect={() => setTokenDialogOpen(true)}
              />
              <LiveTradeFeed
                contracts={portfolio}
                currency={displayBalance?.currency || "USD"}
                authorized={isLive}
                demoMode={isDemo}
                onRefresh={refreshPortfolio}
                onSettled={handleContractSettled}
              />
            </div>
          </div>

          <div style={{ display: activeTab === "hilo" ? "block" : "none" }}>
            <HigherLow
              authorized={isLive}
              accountId={session?.account_id}
              currency={displayBalance?.currency || "USD"}
              onConnect={() => setTokenDialogOpen(true)}
            />
          </div>

          {/* 🎖️ Fleet Commander — REMOVED (owner request: auto-started trades). */}

          {/* 🕹️ Arcade — 4 games on real Even/Odd contracts. */}
          <div style={{ display: activeTab === "arcade" ? "block" : "none" }}>
            <ArcadeHub
              authorized={isLive}
              accountId={session?.account_id}
              currency={displayBalance?.currency || "USD"}
              isReal={isLive && !isVirtualAccount}
              balance={Number(displayBalance?.balance) || 0}
              onConnect={() => setTokenDialogOpen(true)}
            />
          </div>

          {/* 🎴 HiLo Cards — digits become cards; real Over/Under contracts. */}
          <div style={{ display: activeTab === "cards" ? "block" : "none" }}>
            <HiLoCards
              authorized={isLive}
              accountId={session?.account_id}
              accessToken={session?.token || ""}
              currency={displayBalance?.currency || "USD"}
              onConnect={() => setTokenDialogOpen(true)}
            />
          </div>

          <div style={{ display: activeTab === "trade" ? "block" : "none" }}>
            <>
              {/* ⭐ Flagship pins — Penalty Arcade + Heli (user mandate: these
                  replace the old Rise/Fall recommendation banner). */}
              <button
                data-testid="trade-tab-penalty-banner"
                onClick={() => setActiveTab("arcade")}
                className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 border-emerald-400/50 bg-gradient-to-r from-emerald-500/15 to-vts-surface/40 hover:from-emerald-500/25 transition mb-3 group"
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-white leading-tight">
                    ⚽ Most Played: Penalty Arcade
                  </p>
                  <p className="text-[10px] font-mono text-vts-silver/70 leading-tight">
                    kick = real Even/Odd contract (~×1.95) · goals, saves & streaks · from $0.35 · losses possible
                  </p>
                </div>
                <span className="text-emerald-300 text-[11px] font-mono uppercase tracking-wider group-hover:translate-x-1 transition shrink-0">
                  play →
                </span>
              </button>
              <button
                data-testid="trade-tab-heli-banner"
                onClick={() => setActiveTab("heli")}
                className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 border-amber-400/50 bg-gradient-to-r from-amber-500/15 to-vts-surface/40 hover:from-amber-500/25 transition mb-3 group"
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-white leading-tight">
                    🚁 Fan Favorite: Heli Ride
                  </p>
                  <p className="text-[10px] font-mono text-vts-silver/70 leading-tight">
                    ride the multiplier & cash out before the drop · real contracts · you choose the stake · losses possible
                  </p>
                </div>
                <span className="text-amber-300 text-[11px] font-mono uppercase tracking-wider group-hover:translate-x-1 transition shrink-0">
                  ride →
                </span>
              </button>
              {/* v8.9.124 — SymbolChart removed from Trade tab per user
                  mandate ("remove this chart it is not useful"). The
                  StrategyHeatmap mini-sparklines below already show what
                  matters; the big chart was just visual noise. */}
              {/* v8.9.12k — Prominent Telegram CTA at the TOP of the Trade
                  tab so users see it before they trade. Tied to the
                  shared social link. */}
              <a
                href={TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trade-tab-telegram-cta"
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 border-vts-blue/50 bg-gradient-to-r from-vts-blue/15 to-vts-profit/10 hover:from-vts-blue/20 hover:to-vts-profit/15 transition mb-3 group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-vts-blue/20 border border-vts-blue/50 flex items-center justify-center">
                    <Send className="h-4 w-4 text-vts-blue" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-white leading-tight">
                      Join the VTS Telegram channel
                    </div>
                    <div className="text-[10px] text-vts-silver/70 leading-tight">
                      Daily signals · strategy tips · live community
                    </div>
                  </div>
                </div>
                <span className="text-vts-blue text-[11px] font-mono uppercase tracking-wider group-hover:translate-x-1 transition shrink-0">
                  join →
                </span>
              </a>
              {/* 🔔 Personal Telegram alerts — retention: bank pings, nightly
                  digest, Sunday report card, budget alerts to the user's DM.
                  Only for a CONNECTED Deriv account (alerts key off its id). */}
              <TelegramLinkCard accountId={isLive ? (balance?.loginid || session?.account_id) : null} />
              {/* Weekly cross-platform leaderboard (net profit across Pro Trade,
                  Matches/Differs, Forex bot, Digits & manual). Resets every Monday;
                  the #1 trader is auto-posted to the Telegram channel. */}
              <div className="mb-3">
                <LeaderboardCard accountId={displayAccountId} />
              </div>
              {/* v8.9.123 — Daily target bar (ManualTargetBar) + manual
                  Rise/Fall TradePanel removed per user mandate ("remove
                  that target from the trade panel and also remove the
                  Place trade section (rise and fall)"). The Auto-FX bot
                  + the Live Strategy Heatmap auto-trader already cover
                  the trading flow, so the manual rise/fall panel was
                  duplicate noise. Digit-matches panel below stays. */}
              {/* Over 3 Quick Bot — capped ×2.5 recovery digit bot (Jul 2026 user request) */}
              <Over3RecoveryBot
                authorized={isLive || isDemo}
                demoMode={isDemo}
                currency={displayBalance?.currency || "USD"}
                balance={displayBalance?.balance}
                onTradeSettled={(profit) => recordManualTrade(profit, "digit")}
                onGoToAutoBot={() => setActiveTab("risefall")}
              />
              {/* Over 1/Under 8 bot removed per user request (Jun 2026). */}

              {/* 🏆 Weekly leaderboard — live evidence, not guesses */}
              <BotLeaderboard />
              {/* Strategy Heatmap removed per user mandate (Jun 2026):
                  "the 100 strategies… that's just keeping space". */}
              <LiveTradeFeed
                contracts={portfolio}
                currency={displayBalance?.currency || "USD"}
                authorized={isLive || isDemo}
                demoMode={isDemo}
                onRefresh={refreshPortfolio}
                onSettled={handleContractSettled}
              />
            </>
          </div>

          {/* Auto Bot tab + Signals tab removed per user mandate (Jun 2026):
              the Conservative FX Rise/Fall bot is the only rise/fall bot. */}

          {activeTab === "profile" && (
            <ProfileTab
              session={session}
              isLive={isLive}
              balance={displayBalance}
              onConnect={() => setTokenDialogOpen(true)}
            />
          )}

          {activeTab === "history" && (
            <div className="space-y-4">
              <MyBotStats accountId={session?.account_id} />
              <RecentTradesInline onOpenFullPage={() => navigate("/history")} />
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Persistent live-trade ribbon — visible across every tab while any
          contract is open. Subscribes to proposal_open_contract via the
          authenticated WS so it works for manual + bot + signal trades. */}
      <ActiveTradeRibbon authorized={isLive} />

      {/* Mobile bottom nav — sits flush at the screen bottom (the Emergent
          badge has been removed in /public/index.html so bottom-0 is safe).
          pb-safe respects iPhone home-indicator inset. */}
      <nav
        data-testid="mobile-bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-black/95 backdrop-blur-md border-t border-vts-line pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`mobile-tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={`relative flex flex-col items-center justify-center gap-1 py-2 transition ${
                  active ? "text-vts-blue" : "text-vts-silver/60"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "text-vts-blue" : ""}`} />
                {t.star && <span className="absolute top-0.5 right-2 text-[8px] leading-none">⭐</span>}
                <span className="text-[9px] uppercase tracking-wider font-mono leading-none text-center">
                  {t.label}
                </span>
                {active && (
                  <span className="absolute bottom-0 h-0.5 w-8 bg-vts-blue rounded-t" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
